import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { IntlProvider } from 'react-intl'
import { DEFAULT_LOCALE, type SupportedLocale } from './util/locale'
import { isSupportedLocale } from './util/localeMatcher'
import { usePseudoloc } from './PseudolocContext'
// TODO(cungminh2710): Add other languages
import enMessages from './lang/en.json'

type MessageEntry = {
  defaultMessage: string
  description?: string
}

const defaultMessages: Record<string, string> = Object.fromEntries(
  Object.entries(enMessages as Record<string, MessageEntry>).map(([key, value]) => [
    key,
    value.defaultMessage,
  ]),
)

interface I18nProviderProps {
  messages?: Record<string, string>
}

export function I18nProvider({
  messages = defaultMessages,
  children,
}: PropsWithChildren<I18nProviderProps>) {
  const geoLocale = useAppSelector((state) => state.geo.locale)
  const locale: SupportedLocale = geoLocale && isSupportedLocale(geoLocale) ? geoLocale : DEFAULT_LOCALE
  const { enabled: pseudolocEnabled } = usePseudoloc()
  const [pseudolocalizeMessages, setPseudolocalizeMessages] = useState<
    ((messages: Record<string, string>) => Record<string, string>) | null
  >(null)

  useEffect(() => {
    if (pseudolocEnabled) {
      import('./util/pseudoloc').then((module) => {
        setPseudolocalizeMessages(() => module.pseudolocalizeMessages)
      })
    }
  }, [pseudolocEnabled])

  const processedMessages = useMemo(() => {
    if (pseudolocEnabled && pseudolocalizeMessages) {
      return pseudolocalizeMessages(messages)
    }
    return messages
  }, [messages, pseudolocEnabled, pseudolocalizeMessages])

  return (
    <IntlProvider
      locale={locale}
      messages={processedMessages}
      defaultLocale={DEFAULT_LOCALE}
      onError={(err) => {
        // Suppress missing translation warnings in development
        if (err.code === 'MISSING_TRANSLATION') {
          return
        }
        console.error(err)
      }}
    >
      {children}
    </IntlProvider>
  )
}
